import type { StoredUsers } from '~/gql/users/users'
import { useQuery } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_USER_SESSIONS } from '~/gql/sessions/sessions'
import { client } from '~/helpers/apollo'

type CompletionStatus = 'COMPLETED' | 'INCOMPLETE' | 'NOT_ATTEMPTED' | 'UNKNOWN'

type IProps = {
  open: boolean
  onCancel: () => void
  user: StoredUsers
}

/** Badge de progression selon le statut de complétion SCORM. */
function ProgressBadge({ status }: { status: CompletionStatus }) {
  const { t } = useTranslation()
  switch (status) {
    case 'COMPLETED':
      return <Badge variant="success">{t('status.completed')}</Badge>
    case 'INCOMPLETE':
      return <Badge variant="warning">{t('home.inProgress')}</Badge>
    default:
      return <Badge variant="secondary">{t('status.not_attempted')}</Badge>
  }
}

function UserSessionsModal({ open, onCancel, user }: IProps) {
  const { t } = useTranslation()

  const { data, loading } = useQuery(GQL_USER_SESSIONS, {
    client,
    fetchPolicy: 'cache-and-network',
    skip: !open,
    variables: { userId: user.id },
  })

  const sessions = data?.userSessions ?? []

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('user.sessions.title')}</DialogTitle>
          <DialogDescription>
            {t('user.sessions.subtitle', {
              name: `${user.firstName} ${user.lastName}`.trim() || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        {loading && sessions.length === 0
          ? (
              <div className="flex justify-center p-10">
                <Loader className="size-6 animate-spin" />
              </div>
            )
          : sessions.length === 0
            ? (
                <p className="rounded-md border border-dashed border-border p-8 text-center text-muted-foreground">
                  {t('user.sessions.empty')}
                </p>
              )
            : (
                <div className="max-h-[60vh] overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('sessions.name')}</TableHead>
                        <TableHead>{t('common.module')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                        <TableHead>{t('user.sessions.progress')}</TableHead>
                        <TableHead>{t('common.score')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((s) => {
                        const status = (s.myTracking?.completionStatus ?? 'NOT_ATTEMPTED') as CompletionStatus
                        const score = s.myTracking?.scoreRaw
                        return (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium">{s.name}</TableCell>
                            <TableCell>{s.module?.title}</TableCell>
                            <TableCell>
                              <Badge variant={s.status === 'ACTIVE' ? 'default' : 'secondary'}>
                                {s.status === 'ACTIVE' ? t('status.active') : t('status.archived')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <ProgressBadge status={status} />
                            </TableCell>
                            <TableCell>
                              {score != null
                                ? `${score}${s.myTracking?.scoreMax != null ? ` / ${s.myTracking.scoreMax}` : ''}`
                                : '/'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
      </DialogContent>
    </Dialog>
  )
}

export default UserSessionsModal
